import {
  apptsForDay,
  DAY_NAMES_SHORT,
  dateKeyOf,
  dayNameShort,
  deriveSlot,
  gridAppts,
  gridHours,
  monthCells,
  statusMeta,
  typeLabel,
  weekDays,
} from './agenda-adapter';

import type { AppointmentWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

function firstName(title: string): string {
  return title.split(' ')[0] ?? title;
}

/** Statuses que a grade trata como compromisso vivo — sem prefixo de status no texto acessível. */
const LIVE_STATUSES: ReadonlySet<AppointmentWireItem['status']> = new Set<
  AppointmentWireItem['status']
>(['scheduled', 'confirmed', 'imminent']);

/**
 * Texto acessível do chip (`title`). N2-09: cor sozinha não distingue «Cancelado» de
 * «Confirmado» — fora dos statuses vivos, o rótulo do status abre o texto.
 */
function chipTitle(appt: AppointmentWireItem): string {
  const base = `${typeLabel(appt.type)} · ${appt.title}`;
  return LIVE_STATUSES.has(appt.status) ? base : `${statusMeta(appt.status).label} · ${base}`;
}

/** Vista Mês — grade 6×7 com contagem + 2 primeiros do dia. */
export function MonthView({
  cursorMs,
  appts: allAppts,
  nowMs,
  onPick,
}: {
  cursorMs: number;
  appts: AppointmentWireItem[];
  nowMs: number;
  onPick: (dayMs: number) => void;
}): JSX.Element {
  const appts = gridAppts(allAppts); // N2-09
  const cells = monthCells(cursorMs);
  const cursorMonth = new Date(cursorMs).getMonth();
  const todayKey = dateKeyOf(nowMs);
  return (
    <div className="cal-month">
      {DAY_NAMES_SHORT.map((d) => (
        <div key={d} className="cal-month__day-head">
          {d}
        </div>
      ))}
      {cells.map((ms) => {
        const key = dateKeyOf(ms);
        const dayAppts = apptsForDay(appts, key);
        const out = new Date(ms).getMonth() !== cursorMonth;
        const today = key === todayKey;
        const warn = dayAppts.some((a) => a.status === 'scheduled' || a.status === 'no_show');
        return (
          <div
            key={key}
            className={
              'cal-month__day' +
              (out ? ' cal-month__day--out' : '') +
              (today ? ' cal-month__day--today' : '')
            }
            role="button"
            tabIndex={0}
            onClick={() => onPick(ms)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPick(ms);
              }
            }}
          >
            <span className="cal-month__day-num">{new Date(ms).getDate()}</span>
            {dayAppts.length > 0 && (
              <span className={'cal-month__day-pill' + (warn ? ' cal-month__day-pill--warn' : '')}>
                {dayAppts.length} agend.
              </span>
            )}
            {dayAppts.slice(0, 2).map((a) => {
              const slot = deriveSlot(a);
              return (
                <div
                  key={a.id}
                  style={{
                    fontSize: 10,
                    color: 'var(--fg-2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {slot.start} · {firstName(a.title)}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function ApptChip({
  appt,
  variant,
  onAppt,
}: {
  appt: AppointmentWireItem;
  variant: 'week' | 'day';
  onAppt: (appt: AppointmentWireItem) => void;
}): JSX.Element {
  const slot = deriveSlot(appt);
  const tone = statusMeta(appt.status).tone;
  const base = variant === 'week' ? 'cal-week__appt' : 'cal-day__appt';
  return (
    <div
      className={`${base} ${base}--${tone}`}
      role="button"
      tabIndex={0}
      onClick={() => onAppt(appt)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAppt(appt);
        }
      }}
      title={chipTitle(appt)}
    >
      {variant === 'day' ? (
        <>
          <span className="cal-day__appt-time">
            {slot.start}–{slot.end}
          </span>
          <span className="cal-day__appt-title">
            {typeLabel(appt.type)} · {appt.title}
          </span>
        </>
      ) : (
        <>
          <div style={{ fontSize: 10, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
            {slot.start}–{slot.end}
          </div>
          <div
            style={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {firstName(appt.title)}
          </div>
        </>
      )}
    </div>
  );
}

/** Vista Semana — grade hora × dia (8-18h). */
export function WeekView({
  cursorMs,
  appts: allAppts,
  nowMs,
  onAppt,
}: {
  cursorMs: number;
  appts: AppointmentWireItem[];
  nowMs: number;
  onAppt: (appt: AppointmentWireItem) => void;
}): JSX.Element {
  const appts = gridAppts(allAppts); // N2-09: elo reagendado não compete por slot
  const days = weekDays(cursorMs);
  const todayKey = dateKeyOf(nowMs);
  const hours = gridHours(appts, days.map(dateKeyOf));
  return (
    <div className="cal-week">
      <div className="cal-week__corner" />
      {days.map((ms) => {
        const d = new Date(ms);
        const today = dateKeyOf(ms) === todayKey;
        return (
          <div
            key={ms}
            className={'cal-week__day-head' + (today ? ' cal-week__day-head--today' : '')}
          >
            {dayNameShort(d.getDay())}
            <strong>{d.getDate()}</strong>
          </div>
        );
      })}
      {hours.map((h) => (
        <div key={h} style={{ display: 'contents' }}>
          <div className="cal-week__hour">{h}:00</div>
          {days.map((ms) => {
            const key = dateKeyOf(ms);
            const cellAppts = apptsForDay(appts, key).filter((a) => deriveSlot(a).startHour === h);
            const today = key === todayKey;
            return (
              <div key={ms} className={'cal-week__cell' + (today ? ' cal-week__cell--today' : '')}>
                {cellAppts.map((a) => (
                  <ApptChip key={a.id} appt={a} variant="week" onAppt={onAppt} />
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Vista Dia — slots horários (8-18h). */
export function DayView({
  cursorMs,
  appts: allAppts,
  onAppt,
}: {
  cursorMs: number;
  appts: AppointmentWireItem[];
  onAppt: (appt: AppointmentWireItem) => void;
}): JSX.Element {
  const appts = gridAppts(allAppts); // N2-09
  const dayKey = dateKeyOf(cursorMs);
  const dayAppts = apptsForDay(appts, dayKey);
  return (
    <div className="cal-day" style={{ maxWidth: 720 }}>
      {gridHours(appts, [dayKey]).map((h) => {
        const slotAppts = dayAppts.filter((a) => deriveSlot(a).startHour === h);
        return (
          <div key={h} style={{ display: 'contents' }}>
            <div className="cal-day__hour">{h}:00</div>
            <div className="cal-day__slot">
              {slotAppts.length > 0 ? (
                slotAppts.map((a) => <ApptChip key={a.id} appt={a} variant="day" onAppt={onAppt} />)
              ) : (
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)' }}>
                  —
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
